import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthSession } from './imsClient';
import { startSignIn } from './imsClient';

const STORAGE_KEY = 'llm-monitoring-ims-session';

interface TokenContextValue {
  /** Convenience: the access token alone (or null if not signed in) */
  token: string | null;
  session: AuthSession | null;
  setSession: (next: AuthSession | null) => void;
  /** True until we've checked localStorage at least once */
  hydrated: boolean;
  /** Triggers a full sign-out (clears session + redirects to home) */
  signOut: () => void;
}

const TokenContext = createContext<TokenContextValue | null>(null);

/**
 * Re-auth attempt window: if the access token is within this many ms
 * of expiry (or already expired), schedule a silent re-auth redirect.
 * 60s ahead means most users get a fresh token without ever seeing a
 * 401 round-trip.
 */
const SILENT_REAUTH_LEAD_MS = 60_000;

function loadStoredSession(): AuthSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSession;
    if (typeof parsed.accessToken !== 'string' || typeof parsed.expiresAt !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function TokenProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSessionState] = useState<AuthSession | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setSessionState(loadStoredSession());
    setHydrated(true);
  }, []);

  const setSession = useCallback((next: AuthSession | null) => {
    setSessionState(next);
    try {
      if (next) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // localStorage unavailable — token still works for the page lifetime
    }
  }, []);

  const signOut = useCallback(() => {
    setSession(null);
    if (window.location.pathname !== '/') {
      window.location.assign('/');
    }
  }, [setSession]);

  // Schedule a silent re-auth redirect just before the access token expires.
  // The redirect to `prompt=none` is invisible to the user when their Adobe
  // SSO session is still valid (which it almost always is during a workday).
  useEffect(() => {
    if (!session) return;
    const ttl = session.expiresAt - Date.now() - SILENT_REAUTH_LEAD_MS;
    if (ttl <= 0) {
      // Already expired or about to be — go now.
      void startSignIn(window.location.pathname + window.location.search, 'none');
      return;
    }
    const handle = window.setTimeout(() => {
      void startSignIn(window.location.pathname + window.location.search, 'none');
    }, ttl);
    return () => window.clearTimeout(handle);
  }, [session]);

  const value = useMemo<TokenContextValue>(
    () => ({
      token: session?.accessToken ?? null,
      session,
      setSession,
      hydrated,
      signOut,
    }),
    [session, setSession, hydrated, signOut],
  );

  return <TokenContext.Provider value={value}>{children}</TokenContext.Provider>;
}

export function useToken(): TokenContextValue {
  const ctx = useContext(TokenContext);
  if (!ctx) throw new Error('useToken must be used inside <TokenProvider>');
  return ctx;
}
